import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export type EmailForwardingMode = "listen" | "reply";

interface EmailForwardingStepsProps {
  agentId: string;
  address: string;
  /** listen: a Listener genie's listen-only address. reply: a genie that answers the email itself. */
  mode: EmailForwardingMode;
}

interface ForwardingTab {
  value: string;
  label: string;
  steps: ReactNode[];
}

export const copyToClipboard = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Failed to copy");
  }
};

export const Steps = ({ steps }: { steps: ReactNode[] }) => (
  <ol className="space-y-2.5">
    {steps.map((step, index) => (
      <li key={index} className="flex gap-3 text-sm text-gray-700">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-genie-primary/10 text-xs font-semibold text-genie-primary">
          {index + 1}
        </span>
        <span className="pt-0.5">{step}</span>
      </li>
    ))}
  </ol>
);

const listenTabs = (addressChip: ReactNode): ForwardingTab[] => [
  {
    value: "gmail",
    label: "Gmail",
    steps: [
      <>In Gmail, open Settings → See all settings → Forwarding and POP/IMAP.</>,
      <>Click Add a forwarding address and paste {addressChip}.</>,
      <>If Gmail asks for a confirmation code, it appears here within a minute: enter it in Gmail and click Verify. Some accounts forward straight away without one.</>,
      <>Choose Forward a copy of incoming mail and keep Gmail's copy in the inbox. To forward only support mail, use a filter (for example To: support@yourcompany.com) with Forward it to.</>,
    ],
  },
  {
    value: "workspace",
    label: "Workspace",
    steps: [
      <>In the Google Admin console, open Apps → Google Workspace → Gmail → Routing.</>,
      <>Add a routing rule for your support address with Also deliver to {addressChip}.</>,
      <>Save. No confirmation code is needed, and the original mailbox keeps every email.</>,
      <>Prefer setting it up per user? Follow the Gmail steps, after allowing automatic forwarding under Apps → Gmail → End User Access.</>,
    ],
  },
  {
    value: "outlook",
    label: "Outlook",
    steps: [
      <>In Outlook on the web, open Settings → Mail → Rules → Add new rule.</>,
      <>Pick the emails to watch (for example, sent to your support address), then choose Redirect to {addressChip}.</>,
      <>Save. Redirect keeps the customer as the sender, so suggested replies go straight back to them.</>,
      <>On Microsoft 365, an admin may need to allow external forwarding: Defender → Anti-spam outbound policy → Automatic forwarding rules → On.</>,
    ],
  },
];

const replyTabs = (addressChip: ReactNode, logsLink: ReactNode): ForwardingTab[] => [
  {
    value: "gmail",
    label: "Gmail",
    steps: [
      <>In Gmail, open Settings → See all settings → Forwarding and POP/IMAP.</>,
      <>Click Add a forwarding address and paste {addressChip}.</>,
      <>Gmail sends a confirmation email to your genie. Open {logsLink} on this card, copy the code from Gmail's email, enter it in Gmail and click Verify.</>,
      <>Choose Forward a copy of incoming mail and keep Gmail's copy in the inbox. To forward only support mail, use a filter (for example To: support@yourcompany.com) with Forward it to.</>,
    ],
  },
  {
    value: "outlook",
    label: "Outlook / Microsoft 365",
    steps: [
      <>In Outlook on the web, open Settings → Mail → Rules → Add new rule.</>,
      <>Pick the emails to forward (for example, sent to your support address), then choose Redirect to {addressChip}.</>,
      <>Save. Redirect keeps the customer as the sender, so your genie's answer goes straight back to them.</>,
      <>On Microsoft 365, an admin may need to allow external forwarding: Defender → Anti-spam outbound policy → Automatic forwarding rules → On.</>,
    ],
  },
  {
    value: "other",
    label: "Other",
    steps: [
      <>In your email settings, find Forwarding, Rules or Filters.</>,
      <>Forward or redirect your support address to {addressChip}. Choose redirect if it's offered, so the customer stays the sender.</>,
      <>If your provider sends a confirmation email, it shows up in {logsLink} within a minute.</>,
      <>Send a test email to your support address and check it reaches {logsLink}.</>,
    ],
  },
];

/** Per-provider steps for forwarding a support inbox to a genie's mailbox address. */
export const EmailForwardingSteps = ({ agentId, address, mode }: EmailForwardingStepsProps) => {
  const addressChip = (
    <button
      type="button"
      onClick={() => copyToClipboard(address, "Forwarding address")}
      className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-900 hover:bg-gray-200"
    >
      {address}
      <Copy className="h-3 w-3 text-gray-500" />
    </button>
  );
  const logsLink = (
    <Link
      to={`/genie-hub/my-genies/${agentId}/mailbox-logs`}
      className="font-semibold text-genie-primary hover:underline"
    >
      Logs
    </Link>
  );
  const tabs = mode === "listen" ? listenTabs(addressChip) : replyTabs(addressChip, logsLink);

  return (
    <Tabs defaultValue="gmail" className="mt-3">
      {/* "Outlook / Microsoft 365" wraps on narrow screens, so the reply tabs grow to fit it. */}
      <TabsList className={cn("grid w-full grid-cols-3", mode === "reply" && "h-auto min-h-10")}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className={cn(mode === "reply" && "h-full whitespace-normal leading-tight")}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className="pt-3">
          <Steps steps={tab.steps} />
        </TabsContent>
      ))}
    </Tabs>
  );
};
